//https://egghead.io/lessons/remix-expose-specific-environmental-variables-to-the-client-using-remix
import { type LoaderFunctionArgs, type MetaFunction } from "@remix-run/node";
import { Await, defer, useLoaderData } from "@remix-run/react";
import { useTranslation } from "react-i18next";
import { createI18nServer } from "~/i18n.server";
import { authenticator } from "~/services/auth.server";
import { IMAGES_HOST } from "~/services/constants.server";
import { LangLink } from "~/components/common/LangLink";
import Recharge from "~/assets/images/Recharge.svg?url";
import GiftCard from "~/assets/images/Gift cards.svg?url";
import Esim from "~/assets/images/Esim.svg?url";
import Pins from "~/assets/images/Pins.svg?url";
import { getBlogs, getQnA, getSanityClient } from "~/services/sanityClient.server";
import { createClient } from "@sanity/client";
import urlBuilder from "@sanity/image-url";
import { Suspense, useMemo } from "react";
import { PortableText } from "@portabletext/react";
import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react";
import { ChevronDownIcon } from "~/components/icons";
import { SiteCache } from "~/cache/SiteCache.server";
import { VoucherPlacement } from "@client/api-services/client";
import { getSeoTagsByPage } from "~/services/seo.server";

export const meta: MetaFunction<typeof loader> = ({ data }) => {
	return [
		{ title: data?.seoTags.title },
		{ name: "description", content: data?.seoTags.description },
		{ name: "keywords", content: data?.seoTags.keywords },
	];
};

export async function loader({ request, params }: LoaderFunctionArgs) {
	const user = await authenticator.getUser(request);
	const i18nServer = await createI18nServer(request);
	const t = await i18nServer.getFixedT(request);
	const sanityClient = await getSanityClient(request);
	const blogs = getBlogs(request);
	const qnAs = getQnA(request);
	const siteSettings = await SiteCache.getSettings(request);
	const siteId = siteSettings.siteId;
	const seoTags = await getSeoTagsByPage(request, "home");
	// const texts = dic[43];
	return defer({
		imageHost: IMAGES_HOST, user, blogs, qnAs,
		seoTags,
		// title: t("title"),
		// description: t("description"),
		sanity: siteSettings.sanitySettings,
		showVoucherButton: siteSettings.supportsVoucher && siteSettings.voucherPlacement !== VoucherPlacement.Payment,
	});
}
export default function Index() {
	const { imageHost, blogs: blogsPromise, qnAs: qnAsPromise, sanity: sanitySettings, showVoucherButton } = useLoaderData<typeof loader>();
	const builder = useMemo(() => urlBuilder(createClient(sanitySettings)), [sanitySettings]);
	const { t } = useTranslation();

	return <div>
		<div className="p-4 lg:pr-96 bg-no-repeat bg-right-bottom lg:bg-index-hero lg:bg-[length:30rem]">
			<div className="text-3xl md:text-5xl text-slate-700 font-bold">
				{t("home.big_header.web")}
			</div>
			{/* <div className="text-3xl md:text-7xl text-slate-700 font-bold">Top up your phone.</div> */}
			<div className="my-4 md:my-10 text-lg text-zinc-700/70 font-normal">
				{t("home.sub_header.web")}
			</div>
			<div className="my-4 md:my-10 grid grid-cols-2 md:grid-cols-[12rem_12rem_1fr] gap-2">
				<LangLink to="/shop/top-up" className="btn-dark-lg whitespace-nowrap text-sm lg:text-base leading-6">{t("common.go_to_shop")}</LangLink>
				{showVoucherButton ?
					<LangLink to="/voucher" className="btn-dark-lg whitespace-nowrap text-sm lg:text-base leading-6 !px-4">
						{t("common.redeem_voucher")}
					</LangLink>
					: <LangLink to="/shop/gift-cards" className="btn-dark-lg whitespace-nowrap text-sm lg:text-base leading-6">
						{t("common.gift_cards")}
					</LangLink>}
			</div>
			<div className="my-10 flex gap-2">
				<div className="flex items-center justify-center h-10 w-16 bg-neutral-100">
					<img className="w-11 h-6" alt="Visa" src={`${imageHost}/credit-cards/visa.svg`} />
				</div>
				<div className="flex items-center justify-center h-10 w-16 bg-neutral-100">
					<img className="w-11 h-6" alt="Master card" src={`${imageHost}/credit-cards/master-card.svg`} />
				</div>
				<div className="flex items-center justify-center h-10 w-16 bg-neutral-100">
					<img className="w-11 h-6" alt="Amex" src={`${imageHost}/credit-cards/amex.svg`} />
				</div>
				<div className="flex items-center justify-center h-10 w-16 bg-neutral-100">
					<img className="w-11 h-6" alt="Discover" src={`${imageHost}/credit-cards/discover.svg`} />
				</div>
				<div className="hidden lg:flex items-center justify-center h-10 w-16 bg-neutral-100">
					<img className="w-11 h-6" alt="JCB" src={`${imageHost}/credit-cards/jcb.svg`} />
				</div>
				<div className="hidden lg:flex items-center justify-center h-10 w-16 bg-neutral-100">
					<img className="w-11 h-6" alt="Paypal" src={`${imageHost}/credit-cards/paypal.svg`} />
				</div>
			</div>
		</div>
		<div className="my-10">
			<div className="font-bold text-4xl text-center mb-8">
				<label>{t("home.why")}</label>
			</div>
			<div className="text-center md:px-48 text-base mb-8">
				{t("home.why_details")}
			</div>
			<div className="grid grid-cols-1 md:grid-cols-4 gap-2">
				<div className="bg-sky-100 rounded-2xl p-10">
					<div className="bg-white rounded-lg inline-block p-3 mb-4">
						<img src={Recharge} className="size-8" alt="Recharge" />
					</div>
					<div className="font-semibold text-lg mb-4">{t("home.card1.header")}</div>
					<div className="font-normal text-sm">
						{t("home.card1.text")}
					</div>
				</div>
				<div className="bg-rose-50  rounded-2xl p-10">
					<div className="bg-white rounded-lg inline-block p-3 mb-4">
						<img src={GiftCard} className="size-8" alt="Gift card" />
					</div>
					<div className="font-semibold text-lg mb-4">{t("home.card2.header")}</div>
					<div className="font-normal text-sm">
						{t("home.card2.text")}
					</div>
				</div>
				<div className="bg-violet-100  rounded-2xl p-10">
					<div className="bg-white rounded-lg inline-block p-3 mb-4">
						<img src={Pins} className="size-8" alt="Data plan" />
					</div>
					<div className="font-semibold text-lg mb-4">{t("home.card3.header")}</div>
					<div className="font-normal text-sm">
						{t("home.card3.text")}
					</div>
				</div>
				<div className="bg-sky-100  rounded-2xl p-10">
					<div className="bg-white rounded-lg inline-block p-3 mb-4">
						<img src={Esim} className="size-8" alt="Satisfaction Guarantee" />
					</div>
					<div className="font-semibold text-lg mb-4">{t("home.card4.header")}</div>
					<div className="font-normal text-sm">
						{t("home.card4.text")}
					</div>
				</div>
			</div>
		</div>
		<div className="font-bold text-4xl text-center mb-8">
			<label>{t("common.blogs")}</label>
		</div>
		<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 text-sm">
			<Suspense fallback={null}>
				<Await resolve={blogsPromise}>
					{blogs => <>{blogs.map(b => {
						const url = builder
							.image(b.image)
							.auto("format")
							.url();
						return <div key={b.id} className="rounded-3xl border border-gray-100">
							<div className="h-56 bg-cover bg-no-repeat rounded-t-3xl" style={{ backgroundImage: `url(${url})` }} />
							<div className="p-4">
								<div className="text-lg font-semibold mb-4">{b.title}</div>
								<div className="text-balance text-justify line-clamp-2">
									<PortableText value={b.subTitle} />
								</div>
								<LangLink to={`/blog/${b.slug}`} className="flex items-center gap-2 mt-2">
									<span>{t("home.read_more")}</span>
									<span className="text-lg">➝</span>
								</LangLink>
							</div>
						</div>
					})
					}</>}
				</Await>
			</Suspense>
		</div>
		<div className="grid grid-cols-1 lg:grid-cols-2 mt-6 gap-10">
			<Suspense fallback={null}>
				<Await resolve={qnAsPromise}>
					{(qnAs) => <>
						<div className="text-slate-700">
							<div className="text-4xl font-bold mb-4">{t("common.faqs")}</div>
							<div className="text-sm opacity-60">
								{t("home.faqs_details")}
							</div>
						</div>
						<div className="flex flex-col gap-2">
							{qnAs?.qnaList?.map(qna =>
								<Disclosure key={qna.id} as="div" className="border border-gray-100 rounded-2xl p-4 flex flex-col gap-2">
									<DisclosureButton className="group flex gap-x-2 w-full items-center justify-between">
										<span className="text-left text-lg font-semibold text-slate-700 group-data-[hover]:text-slate-700/80">
											{qna.question}
										</span>
										<ChevronDownIcon className="shrink-0 size-5 font-semibold fill-slate-700/60 group-data-[hover]:fill-slate-700/50 group-data-[open]:rotate-180" />
									</DisclosureButton>
									<DisclosurePanel transition className="border-t border-t-gray-100 pt-4 opacity-60 origin-top transition duration-200 ease-out data-[closed]:-translate-y-6 data-[closed]:opacity-0">
										<PortableText value={qna.answer} />
									</DisclosurePanel>
								</Disclosure>)}
						</div>
					</>}
				</Await>
			</Suspense>
		</div>
		{/* <Disclosure>
			<DisclosurePanel transition className="origin-top transition duration-200 ease-out data-[closed]:-translate-y-6 data-[closed]:opacity-0">
				Yes! You can purchase a license that you can share with your entire team.
			</DisclosurePanel>
		</Disclosure> */}
	</div>
}